import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------------- Token store ----------------
// Bearer-fallback för iPad Safari + cross-origin iframe (Emergent App builder
// preview-pane) där ITP blockerar third-party cookies. På första-partens
// produktions-domän app.trappkonsulten.se lagras normalt cookies OCH bearer –
// backenden accepterar båda (första lyckade räknas). Ingen försämring för
// befintlig cookie-drift.
const ACCESS_KEY = "tk_access_token";
const REFRESH_KEY = "tk_refresh_token";

export const tokenStore = {
  getAccess: () => {
    try { return localStorage.getItem(ACCESS_KEY) || null; } catch { return null; }
  },
  getRefresh: () => {
    try { return localStorage.getItem(REFRESH_KEY) || null; } catch { return null; }
  },
  set: (access, refresh) => {
    try {
      if (access) localStorage.setItem(ACCESS_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    } catch { /* privatlägen kan blockera */ }
  },
  clear: () => {
    try {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } catch { /* noop */ }
  },
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Injicera Authorization-header när vi har en lagrad token. Cookie-baserad
// auth fortsätter fungera parallellt; backenden läser cookie först, header
// som fallback.
api.interceptors.request.use((config) => {
  const t = tokenStore.getAccess();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// ---------------- Auto-refresh interceptor ----------------
// När access-token har gått ut (401) försöker vi `/auth/refresh` EN gång och
// kör om det ursprungliga anropet. Undantag: auth-endpoints själva (login,
// refresh, logout, register) rör vi inte — de får bubbla upp.
//
// Om refresh misslyckas skickas ett globalt `auth:expired`-event så AuthContext
// kan trigga utloggning + redirect till /login. Detta undviker att sidor visar
// råa "Request failed with status code 401"-fel i UI:t.
let refreshPromise = null;

const AUTH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/register"];
const isAuthPath = (url = "") => AUTH_PATHS.some((p) => url.includes(p));

async function doRefresh() {
  // Skicka refresh-token både som cookie (om finns) och som X-Refresh-Token
  // header (Bearer-fallback). Backend läser vad som finns.
  const rt = tokenStore.getRefresh();
  const headers = rt ? { "X-Refresh-Token": rt } : {};
  const r = await api.post("/auth/refresh", null, { headers });
  const access = r?.data?.access_token;
  const refresh = r?.data?.refresh_token;
  if (access || refresh) tokenStore.set(access, refresh);
  return r;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !original ||
      original._retry ||
      isAuthPath(original.url || "")
    ) {
      return Promise.reject(error);
    }

    // Om vi redan är på en publik sida (landing, login, register, pricing, etc.)
    // så finns det ingen mening att försöka refresha — låt anropet bara faila
    // tyst. Detta undviker onödig 401-noise och (viktigt!) hindrar publik-
    // sidesbesökare från att force-redirectas till /login när `auth:expired`
    // fyras av det misslyckade refresh-anropet.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const PUBLIC_PATHS = [
      "/",
      "/login",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/pricing",
      "/integrationer",
      "/accept-invite",
      "/privacy",
      "/data-deletion",
    ];
    const onPublicPage =
      PUBLIC_PATHS.includes(path) ||
      path.startsWith("/reset-password") ||
      path.startsWith("/tidrapportering-") ||
      path.startsWith("/ata-signering/");
    if (onPublicPage) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
          // Rensa efter promise settled så nästa refresh-våg får en ny.
          refreshPromise = null;
        });
      }
      await refreshPromise;
      // Efter refresh kan vi ha en ny bearer-token – låt request-interceptorn
      // hämta den från tokenStore vid retry. Rensa gammal Authorization så vi
      // inte råkar skicka utgången token.
      if (original.headers && original.headers.Authorization) {
        delete original.headers.Authorization;
      }
      return api(original);
    } catch (refreshErr) {
      // Refresh definitivt misslyckats — signalera global utloggning.
      tokenStore.clear();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:expired"));
      }
      // Strippa "Inte inloggad"-detail så nedströms-komponenter inte poppar
      // en förvirrande röd toast innan redirect till /login hinner ske.
      if (error.response?.data?.detail) {
        error.response.data = { ...error.response.data, detail: null };
      }
      return Promise.reject(error);
    }
  }
);

export default api;
